import apiClient from './config';
import { 
    Contract, 
    GetContractsResponse, 
    GetContractResponse, 
    UploadContractResponse, 
    SignContractResponse, 
    ArchiveContractResponse,
    DownloadResponse,
    GetContractsParams,
    DownloadParams,
    SendSignatureLinkRequest,
    SendSignatureLinkResponse,
    GenerateContractResponse
} from '../../types/contract';

// ========================================
// SERVICES PARA CONTRATOS
// ========================================

/**
 * Subir un nuevo contrato (PDF)
 * POST /api/contratos/upload
 */
export const uploadContract = async (
    contractFile: File,
    clientId: string,
    subscriptionId: string,
    createdBy: string
): Promise<UploadContractResponse> => {
    const formData = new FormData();
    formData.append('contract', contractFile);  // req.file en multer
    formData.append('cliente_id', clientId);    // Backend espera cliente_id
    formData.append('suscripcion_id', subscriptionId); // Backend espera suscripcion_id
    formData.append('createdBy', createdBy);

    const response = await apiClient.post('/contratos/upload', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

/**
 * Generar contrato PDF automáticamente
 * POST /api/contratos/generar
 */
export const generateContract = async (
    clientId: string,
    subscriptionId: string,
    createdBy: string
): Promise<GenerateContractResponse> => {
    console.log('🌐 generateContract called with:', { clientId, subscriptionId, createdBy });
    
    try {
        const response = await apiClient.post('/contratos/generar', {
            cliente_id: clientId,
            suscripcion_id: subscriptionId,
            createdBy
        });
        
        console.log('✅ generateContract response:', response.data);
        return response.data;
    } catch (error) {
        console.error('❌ generateContract error:', error);
        throw error;
    }
};

/**
 * Subir firma para un contrato existente
 * POST /api/contratos/:contractId/firmar
 */
export const uploadSignature = async (
    contractId: string,
    signatureFile: File,
    firmanteDni: string,
    updatedBy: string,
    clientEmail?: string // Opcional para envío de confirmación
): Promise<SignContractResponse> => {
    const formData = new FormData();
    formData.append('signature', signatureFile);
    formData.append('firmanteDni', firmanteDni);
    formData.append('updatedBy', updatedBy);
    
    // Agregar email si está disponible
    if (clientEmail) {
        formData.append('clientEmail', clientEmail);
    }

    const response = await apiClient.post(`/contratos/${contractId}/firmar`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

/**
 * Obtener todos los contratos de un cliente
 * GET /api/contratos/cliente/:clientId
 */
export const getContractsByClient = async (
    clientId: string,
    params?: GetContractsParams
): Promise<GetContractsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.includeInactive) {
        queryParams.append('includeInactive', 'true');
    }

    const url = `/contratos/cliente/${clientId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    
    const response = await apiClient.get(url);
    return response.data; // Ya es GetContractsResponse con message, contracts, count
};

/**
 * Obtener el contrato activo de un cliente
 * GET /api/contratos/activo/:clientId
 */
export const getActiveContract = async (clientId: string): Promise<Contract | null> => {
    try {
        const response = await apiClient.get(`/contratos/activo/${clientId}`);
        const data: GetContractResponse = response.data;
        return data.contract;
    } catch (error: any) {
        // Si es 404, no hay contrato activo - esto es válido
        if (error.response?.status === 404) {
            return null;
        }
        throw error;
    }
};

/**
 * Generar URL de descarga temporal para contrato o firma
 * GET /api/contratos/:contractId/descargar
 */
export const getContractDownload = async (
    contractId: string,
    params?: DownloadParams
): Promise<DownloadResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.type) {
        queryParams.append('type', params.type);
    }

    const url = `/contratos/${contractId}/descargar${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
};

/**
 * Archivar un contrato (mover a backups)
 * PUT /api/contratos/:contractId/archivar
 */
export const archiveContract = async (
    contractId: string,
    updatedBy: string,
    reason?: string
): Promise<Contract> => {
    const response = await apiClient.put(`/contratos/${contractId}/archivar`, {
        updatedBy,
        reason
    });
    const data: ArchiveContractResponse = response.data;
    return data.contract;
};

/**
 * Enviar link de firma remota por email
 * POST /api/contratos/:contractId/enviar-link
 */
export const sendSignatureLink = async (
    contractId: string,
    request: SendSignatureLinkRequest
): Promise<SendSignatureLinkResponse> => {
    console.log('🌐 sendSignatureLink called with:', { contractId, request });
    
    try {
        const response = await apiClient.post(`/contratos/${contractId}/enviar-link`, {
            expirationHours: request.expirationHours || 48, // Default 48 horas
            sendToCentro: request.sendToCentro || false // Default: enviar al firmante
        });
        
        console.log('✅ sendSignatureLink response:', response.data);
        return response.data;
    } catch (error) {
        console.error('❌ sendSignatureLink error:', error);
        throw error;
    }
};

// ========================================
// FUNCIONES AUXILIARES
// ========================================

/**
 * Validar si un archivo es PDF válido
 */
export const validatePdfFile = (file: File): { isValid: boolean; error?: string } => {
    if (!file) {
        return { isValid: false, error: 'No se ha seleccionado ningún archivo' };
    }

    if (file.type !== 'application/pdf') {
        return { isValid: false, error: 'Solo se permiten archivos PDF' };
    }

    // 15MB máximo
    const maxSize = 15 * 1024 * 1024;
    if (file.size > maxSize) {
        return { isValid: false, error: 'El archivo no puede superar los 15MB' };
    }

    return { isValid: true };
};

/**
 * Validar si un archivo es imagen válida para firma
 */
export const validateSignatureFile = (file: File): { isValid: boolean; error?: string } => {
    if (!file) {
        return { isValid: false, error: 'No se ha seleccionado ningún archivo' };
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
        return { isValid: false, error: 'Solo se permiten archivos JPG, PNG o SVG' };
    }

    // 2MB máximo
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
        return { isValid: false, error: 'El archivo no puede superar los 2MB' };
    }

    return { isValid: true };
};